import * as THREE from "three";

type VariantMapping = {
  material: number;
  variants: number[];
};

type ApplyVariantParams = {
  model: THREE.Object3D;
  variantName: string;
  // Variant names in declaration order (from KHR_materials_variants).
  variantNames: string[];
  // GLB materials indexed by their glTF material index.
  materialsByIndex: THREE.Material[];
};

/**
 * Switches the model to a named `KHR_materials_variants` variant.
 *
 * three.js r176 has no built-in support for this extension, so the per-primitive
 * mapping table is read from `mesh.userData.gltfExtensions.KHR_materials_variants`
 * (GLTFLoader stores unknown extensions there) and the mapped material — resolved
 * by index at load time — is assigned to each affected mesh.
 *
 * Unlike `switchMaterials`, this respects the model's authored per-node materials
 * (e.g. cushion vs. seat get different fabrics within one variant) and does not
 * force a matte surface, so intentionally metallic parts (metal legs) stay metal.
 *
 * Only meshes that the requested variant actually maps are changed; meshes it
 * does not map are left as-is. This lets several independent variant dimensions
 * (e.g. a "fabric-*" variant and a "leg-*" variant, both in one variants set)
 * be applied one after another without each call resetting the others.
 */
export function applyVariant(params: ApplyVariantParams): boolean {
  const { model, variantName, variantNames, materialsByIndex } = params;

  const variantIndex = variantNames.indexOf(variantName);
  if (variantIndex === -1) {
    console.warn(
      `applyVariant: variant "${variantName}" not declared in the model`,
    );
    return false;
  }

  let applied = false;
  model.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    const userData = object.userData as {
      gltfExtensions?: {
        KHR_materials_variants?: { mappings?: VariantMapping[] };
      };
    };

    const ext = userData.gltfExtensions?.KHR_materials_variants;
    if (!ext?.mappings) return;

    // Only touch primitives this variant maps; leave the rest untouched so
    // other variant dimensions applied earlier are preserved.
    const mapping = ext.mappings.find((m) => m.variants.includes(variantIndex));
    if (!mapping) return;

    const material = materialsByIndex[mapping.material];
    if (material) {
      object.material = material;
      applied = true;
    }
  });

  return applied;
}
